import {getType, ActionType} from 'typesafe-actions';
import * as _ from 'lodash';
import * as actions from './actions';
import {TablesState, TableState, Selection, isSelected, isAnySelected, adjustSelectionHelper} from './actions';
import update from 'immutability-helper';
import {Map} from 'immutable';
import {getVisibleColumns} from './selectors';
import {getChildrenIndices, getParentIndex, isChildRow, updateDataIfHasChildren} from '../VirtualizedHelpers';
export const SELECTION_NONE: Selection = {include: []};
export const SELECTION_ALL: Selection = {exclude: []};

export default function reducer(state: TablesState = {}, action: ActionType<typeof actions>): TablesState {
  let tableState: TableState;
  let name: string;
  if (action.payload) {
    name = action.payload.name;
    tableState = state[name];
    if (name && !tableState && action.type !== getType(actions.setInitialTableState)) {
      // avoid errors when table is not mounted.
      return state;
    }
  }
  switch (action.type) {
    case getType(actions.setInitialTableState):
      const visibleColumns = getVisibleColumns(action.payload.state);
      return update(state, {
        [name]: {
          $set: {
            filterOpen: false,
            filter: {},
            selection: SELECTION_NONE,
            data: Map(),
            dataUpdates: Map(),
            visibleColumns: action.payload.showAllVisibleColumns ? visibleColumns : visibleColumns.slice(0, 8),
            virtualized: true,
            ...action.payload.state,
          },
        },
      });
    case getType(actions.deleteTable):
      return _.omit(state, name);
    case getType(actions.setRows):
      state = update(state, {
        [name]: {
          data: {
            $apply: (data: TableState['data']) => {
              return updateDataIfHasChildren(
                (action.payload.reset ? Map() : data).withMutations(data => {
                  action.payload.rows.forEach((rowData, index) => data.set(action.payload.startIndex + index, rowData));
                }) as any,
                tableState.columnsConfig,
                action.payload.supportChildren
              );
            },
          },
          count: !_.isUndefined(action.payload.count)
            ? {
                $set: action.payload.count,
              }
            : {$apply: _.identity},
          additionalData: !_.isUndefined(action.payload.additionalData)
            ? {
                $set: action.payload.additionalData,
              }
            : {$apply: _.identity},
        },
      });
      if (action.payload.reset) {
        // adjust selection for deleted rows.
        tableState = state[name];
        const idsToKeep = tableState.data.map(rowData => rowData[tableState.columnsConfig.id]).toArray();
        state = adjustSelectionHelper(state, name, idsToKeep, tableState.count);
      }
      return state;
    case getType(actions.updateRows):
      return update(state, {
        [name]: {
          dataUpdates: {
            $apply: (dataUpdates: TableState['dataUpdates']) => {
              return dataUpdates.withMutations(dataUpdates => {
                tableState.data.forEach((rowData, index) => {
                  if (isSelected({...tableState, selection: action.payload.selection}, index)) {
                    const updatedValues = {};
                    const parentIndex = getParentIndex(rowData);
                    const isChild = isChildRow(rowData);
                    const skipChild = isChild && !tableState.columns[action.payload.columnName].childField;
                    if (!skipChild) {
                      updatedValues[index] = action.payload.updater(rowData);
                      if (isChild) {
                        updatedValues[parentIndex] = null;
                      }
                      _.forEach(getChildrenIndices(rowData), childIndex => {
                        updatedValues[childIndex] = null;
                      });
                    }
                    _.forEach(updatedValues, (updatedValue, id: any) => {
                      id = _.toNumber(id);
                      if (dataUpdates.has(id)) {
                        if (updatedValue === undefined) {
                          dataUpdates.update(id, rowDataUpdates => _.omit(rowDataUpdates, action.payload.columnName));
                        } else {
                          dataUpdates.update(id, rowDataUpdates => ({
                            ...rowDataUpdates,
                            [action.payload.columnName]: updatedValue,
                          }));
                        }
                      } else {
                        if (updatedValue !== undefined) {
                          dataUpdates.set(id, {[action.payload.columnName]: updatedValue});
                        }
                      }
                    });
                  }
                });
              });
            },
          },
        },
      });
    case getType(actions.setSortProps):
      return update(state, {
        [name]: {
          sortProps: {
            $set: action.payload.sortProps,
          },
          ...(tableState.virtualized && {
            data: {
              $set: Map(),
            },
          }),
        },
      });
    case getType(actions.setVisibleRange):
      return update(state, {
        [name]: {
          visibleRange: {
            $set: action.payload.visibleRange,
          },
        },
      });
    case getType(actions.triggerTableRefresh):
      return update(state, {
        [name]: {
          tableRefresh: {
            $set: true,
          },
        },
      });
    case getType(actions.clearTableRefresh):
      return update(state, {
        [name]: {
          tableRefresh: {
            $set: false,
          },
        },
      });
    case getType(actions.toggleSelectAll):
      return update(state, {
        [name]: {
          selection: {
            $set: isAnySelected(tableState) ? SELECTION_NONE : SELECTION_ALL,
          },
        },
      });
    case getType(actions.toggleSelect):
      const {index} = action.payload;
      const id = tableState.data.get(index)[tableState.columnsConfig.id];
      return update(state, {
        [name]: {
          selection: {
            $apply: ({include, exclude}: Selection) => {
              if (include) {
                include = isSelected(tableState, index) ? _.filter(include, value => value !== id) : [...include, id];
              } else {
                exclude = isSelected(tableState, index) ? [...exclude, id] : _.filter(exclude, value => value !== id);
              }
              return {include, exclude};
            },
          },
        },
      });
    case getType(actions.setFilter):
      return update(state, {
        [name]: {
          filter: {$set: action.payload.filter},
          selection: {$set: SELECTION_NONE},
          data: {$set: Map()},
          count: {$set: undefined},
          tableRefresh: {$set: true},
          filterRenderButtons: {$set: true},
        },
      });
    case getType(actions.clearFilter):
      return update(state, {
        [name]: {
          filter: {$set: {}},
          selection: {$set: SELECTION_NONE},
          data: {$set: Map()},
          count: {$set: undefined},
          tableRefresh: {$set: true},
          filterRenderButtons: {$set: false},
        },
      });
    case getType(actions.setVisibleColumns):
      return update(state, {
        [name]: {
          visibleColumns: {$set: action.payload.visibleColumns},
        },
      });
    case getType(actions.setAdditionalData):
      return update(state, {
        [name]: {
          additionalData: {
            $apply: additionalData => ({...additionalData, [action.payload.key]: action.payload.value}),
          },
        },
      });
    case getType(actions.setFilterOpen):
      return update(state, {
        [name]: {
          filterOpen: {
            $set: action.payload.open,
          },
        },
      });
    case getType(actions.adjustSelection):
      return adjustSelectionHelper(state, name, action.payload.ids, action.payload.count);
    case getType(actions.setData):
      const {data} = action.payload;
      return update(state, {
        [name]: {
          data: {$set: data},
          count: {$set: data.size},
        },
      });
    case getType(actions.setColumns):
      const {columns, resetVisibleColumns} = action.payload;
      const query = {
        [name]: {
          columns: {$set: columns},
          visibleColumns: {$set: getVisibleColumns({...tableState, columns})},
        },
      };
      if (!resetVisibleColumns) {
        delete query[name].visibleColumns;
      }
      return update(state, query);
  }
  return state;
}
